/**
 * Google Analytics 4 (gtag.js) — lazy, runtime loader. Mirrors the `src/utils/stripe.ts`
 * pattern: the `<script>` is injected on the FIRST call, never at module-eval time, so GA is
 * fetched only after the owner has granted marketing consent (the gate lives in
 * `src/components/analytics-consent`). The measurement id comes from
 * `window.config.googleAnalyticsId` (baked in `ini.js`, templated per environment by Helm).
 *
 * Page views are owned by the app-wide route effect (`trackPageView`); GA4 is initialised
 * with `send_page_view: false` so the automatic view from `config` doesn't double-count the
 * first load.
 */

import { scrubAnalyticsUrl } from './sanitize';
import { ANALYTICS_REDACT_SELECTOR } from './trackingOptOut';

// Injected once per page, at module scope so it survives React remounts (router rebuilds).
let injected = false;

/** GA disable flag key — GA4 reads `window['ga-disable-<id>']` dynamically to stop sending. */
const disableKey = (id: string): string => `ga-disable-${id}`;

/**
 * The gtag stub (defined synchronously before gtag.js loads so early calls queue).
 * It MUST push the real `arguments` object, exactly like Google's snippet: gtag.js only
 * treats a `dataLayer` entry as a command (`js`/`config`/`event`) when it's an `arguments`
 * object — push a plain array and the command is read as a data push and silently ignored
 * (GA appears to "not fire" even though the script loaded).
 */
function gtagStub(): void {
  // eslint-disable-next-line prefer-rest-params
  (window.dataLayer = window.dataLayer ?? []).push(arguments);
}

/**
 * Pin GA4's auto-collected page URL to a scrubbed value. gtag auto-attaches
 * `page_location` (the live `window.location.href`) to EVERY hit — conversions and click
 * events included — so scrubbing only the explicit page_view fields would still leak a
 * sensitive param (e.g. the magic-link token) on any other event fired while it is in the
 * URL. `gtag('set')` applies to all subsequent hits; refreshed on every SPA page view.
 */
function gaSetScrubbedLocation(): void {
  window.gtag?.('set', { page_location: scrubAnalyticsUrl(window.location.href) });
}

/** Inject gtag.js for `id` once and configure GA4. Idempotent. */
export function loadGoogleAnalytics(id: string): void {
  if (injected || !id) return;
  injected = true;
  // Clear any disable flag a prior session may have set (see `setGaEnabled`).
  (window as unknown as Record<string, unknown>)[disableKey(id)] = false;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
  window.gtag = gtagStub;
  window.gtag('js', new Date());
  // The route effect owns SPA page views, so suppress the automatic one from `config`.
  window.gtag('config', id, { send_page_view: false });
  gaSetScrubbedLocation();
  attachClickTracking();
}

/** A clean, bounded label for an interactive element (its text, or aria-label for icon-only). */
const elementLabel = (el: Element): string =>
  ((el.textContent || el.getAttribute('aria-label')) ?? '').replace(/\s+/g, ' ').trim().slice(0, 100);

let clickTrackingAttached = false;

/**
 * Auto-track link + button clicks as GA4 events — parity with the Facebook Pixel's automatic
 * click tracking (GA4 only auto-captures OUTBOUND links via Enhanced Measurement, not internal
 * buttons/links). A single delegated, capture-phase listener (so it sees the click before any
 * navigation) fires `click` for `<a>` and `button_click` for buttons; it reads only, never
 * preventing default, so SPA navigation is untouched. Sent through the disable-gated gtag, so
 * consent-off (`ga-disable-<id>`) suppresses the events — no separate teardown needed.
 */
function attachClickTracking(): void {
  if (clickTrackingAttached) return;
  clickTrackingAttached = true;
  document.addEventListener(
    'click',
    (event) => {
      if (typeof window.gtag !== 'function') return;
      const node = event.target as Element | null;
      const el = node && typeof node.closest === 'function' ? node.closest('a, button, [role="button"]') : null;
      // A container marked `data-analytics-redact` shows the owner's own picks (see `trackingOptOut.ts`): no label leaves.
      if (!el || el.closest(ANALYTICS_REDACT_SELECTOR)) return;
      const anchor = el.closest('a');
      if (anchor) {
        const href = anchor.getAttribute('href') ?? '';
        let linkUrl = href;
        let linkDomain = '';
        let outbound = false;
        try {
          const url = new URL(href, window.location.href);
          linkUrl = url.href;
          linkDomain = url.host;
          outbound = url.host !== window.location.host;
        } catch {
          /* non-navigational href (#, javascript:, mailto:, tel:) — keep the raw value */
        }
        window.gtag('event', 'click', {
          link_text: elementLabel(anchor),
          link_url: scrubAnalyticsUrl(linkUrl),
          link_domain: linkDomain,
          outbound,
        });
      } else {
        window.gtag('event', 'button_click', { button_text: elementLabel(el) });
      }
    },
    true,
  );
}

/**
 * Enable / disable GA collection for `id` WITHOUT unloading it. GA4 honours the
 * `window['ga-disable-<id>']` flag dynamically, so this is how consent-off stops (and a later
 * consent-on resumes) sending. Safe to call even if GA was never loaded — the flag is simply
 * read by GA if/when it loads.
 */
export function setGaEnabled(id: string, enabled: boolean): void {
  if (!id) return;
  (window as unknown as Record<string, unknown>)[disableKey(id)] = !enabled;
}

/** Record an SPA page view. No-ops until the gtag stub is present. */
export function gaPageView(id: string, path: string): void {
  if (!id || typeof window.gtag !== 'function') return;
  gaSetScrubbedLocation();
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: scrubAnalyticsUrl(window.location.href),
    page_title: document.title,
  });
}
